import React, { useState } from "react";

import Button from "@/components/Button";
import { useAdminStore } from "@/stores/adminStore";
import { prepareHeaders } from "@/utils";
import cincolabdataEndpoints from "@/utils/cincolabdata.endpoints";

const AdminAuth = () => {
  const adminStore = useAdminStore();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [mfaCodeSent, setMfaCodeSent] = useState(false);
  const [mfaCode, setMfaCode] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");

  const onLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");

    if (!email || !password) {
      setError("Email and password are required");
      return;
    }

    setIsLoading(true);
    const response = await fetch(cincolabdataEndpoints.auth.login, {
      method: "POST",
      headers: prepareHeaders(),
      mode: "cors",
      redirect: "follow",
      body: JSON.stringify({
        email: email,
        password: password,
      }),
    });

    const data = await response.json();

    if (!response.ok) {
      setIsLoading(false);
      setError(response.statusText);
      return;
    }

    if (data.message !== "MFA Code sent") {
      setIsLoading(false);
      setError("Login failed");
      return;
    }
    setError("");
    setIsLoading(false);
    setMfaCodeSent(true);
    setPassword("");
  };

  const onMFACodeVerify = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setIsLoading(true);

    if (!email || !mfaCode) {
      setError("MFA code is required");
      return;
    }

    const response = await fetch(cincolabdataEndpoints.auth.mfa.verify, {
      method: "POST",
      headers: prepareHeaders(),
      mode: "cors",
      redirect: "follow",
      body: JSON.stringify({
        email: email,
        code: mfaCode,
      }),
    });

    const data = await response.json();
    if (!response.ok) {
      setIsLoading(false);
      setError(data.error || "Login failed");
      return;
    }

    setIsLoading(false);
    adminStore.setAdminLoggedIn(true);
    adminStore.setAdminToken(data.token);
    adminStore.setAdminUser(data.user);
    setEmail("");
  };

  const onMFACodeResend = async () => {
    setError("");
    setIsLoading(true);

    if (!email) {
      setError("Email is required");
      return;
    }

    const response = await fetch(cincolabdataEndpoints.auth.mfa.resend, {
      method: "POST",
      headers: prepareHeaders(),
      mode: "cors",
      redirect: "follow",
      body: JSON.stringify({
        email: email,
      }),
    });
    setIsLoading(false);

    const data = await response.json();
    if (!response.ok || data.error) {
      setError(data.error);
      return;
    }

    setError("");
  };

  const formatMfaCode = (value: string) => {
    // Allow only digits and spaces, preserving spaces
    const sanitized = value.replace(/[^\d ]/g, "");
    // Split into groups of three digits, preserving spaces
    const parts =
      sanitized
        .split(/\s+/)
        .join("")
        .match(/.{1,3}/g) || [];
    return parts.join(" ").slice(0, 7);
  };

  const handleMfaCodeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const formatted = formatMfaCode(e.target.value);
    setMfaCode(formatted);
  };

  if (!mfaCodeSent) {
    return (
      <form
        className="grow flex flex-col py-4 px-5 items-center"
        onSubmit={onLogin}
      >
        <div className="mt-10 mb-5 flex flex-col items-center">
          <p className="text-dark-blue text-center">
            Log in using your cincolabdata account
          </p>
          <input
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            type="email"
            className="border-4 border-blue rounded-full text-dark-blue bg-white mt-2 py-4 px-8 max-w-[400px] w-full"
            placeholder="Email"
          />
          <input
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            type="password"
            className="border-4 border-blue rounded-full text-dark-blue bg-white mt-2 py-4 px-8 max-w-[400px] w-full"
            placeholder="Password"
          />
        </div>

        {error && <p className="mt-4 text-red-500">{error}</p>}

        <Button className="mt-4" type="submit" loading={isLoading}>
          Log in
        </Button>
      </form>
    );
  }

  return (
    <form
      className="grow flex flex-col py-4 px-5 items-center"
      onSubmit={onMFACodeVerify}
    >
      <p className="mt-10 mb-5 text-dark-blue text-center">
        MFA code has been sent to your email
      </p>
      <input
        value={mfaCode}
        onChange={handleMfaCodeChange}
        type="text"
        className="border-4 border-dark-blue rounded-full text-dark-blue bg-white mt-2 py-4 px-8 max-w-[400px] w-full"
        placeholder="MFA Code"
        maxLength={8}
      />

      {error && <p className="mt-4 text-red-500">{error}</p>}

      <div className="flex justify-between items-center w-full">
        <Button
          className="mt-4"
          design="outline-blue"
          type="button"
          onClick={onMFACodeResend}
          disabled={isLoading}
          loading={isLoading}
        >
          Resend
        </Button>
        <Button className="mt-4" type="submit" loading={isLoading}>
          Verify
        </Button>
      </div>
    </form>
  );
};

export default AdminAuth;
